export const PERMISSIONS = Object.freeze({
  DASHBOARD_VIEW: 'common.view_organization_dashboard',
  REPORTS_VIEW: 'common.view_reports',
  USERS_MANAGE: 'common.manage_users',
  ROLES_MANAGE: 'common.manage_roles',
  SUBSCRIPTION_MANAGE: 'common.manage_subscription',
  WHATSAPP_COMMUNICATION_MANAGE: 'common.manage_whatsapp_communication',
  // Hall bookings (Hotel Additional Features). Legacy key kept for compatibility.
  BANQUET_CONFERENCE_MANAGE: 'common.manage_banquet_conference',
  HALL_MANAGE: 'common.manage_banquet_conference',
  BOOKING_OPERATIONS: 'common.manage_booking_operations',
  BOOKING_MODIFY: 'common.modify_booking',
  CUSTOMER_MANAGE: 'common.manage_customer_management',
  HOUSEKEEPING_MANAGE: 'common.manage_housekeeping',
  FINANCE_MANAGE: 'common.manage_finance',
  CONFIGURATION_MANAGE: 'common.manage_configuration',
  INVENTORY_MANAGE: 'common.manage_inventory',
  BOOKING_ENGINE_MANAGE: 'common.manage_booking_engine',
  WEB_CHECKIN_MANAGE: 'common.manage_web_checkin',
  AI_CHECKIN_MANAGE: 'common.manage_ai_checkin',
  AI_INVENTORY_MANAGE: 'common.manage_ai_inventory',
  STOCK_MANAGE: 'common.manage_stock',
  DISTRIBUTION_MANAGE: 'common.manage_distribution',
  CHANNELHUB_MANAGE: 'common.manage_channelhub',
  DYNAMIC_PRICING_MANAGE: 'common.manage_dynamic_pricing',
  POS_RESTAURANT_MANAGE: 'common.manage_pos_restaurant',
  BOOKING_AUDIT_VIEW: 'common.view_booking_audit_logs',
  POS_AUDIT_VIEW: 'common.view_pos_order_audit_logs',
});

export const PERMISSION_DENIED_MESSAGE = 'Access restricted: your role does not include this permission. Please contact the Owner to request access.';

// Org-level permissions are grantable via Additional Access without hotel package gating.
const ORG_LEVEL_PERMISSION_CODES = new Set([
  PERMISSIONS.DASHBOARD_VIEW,
  PERMISSIONS.USERS_MANAGE,
  PERMISSIONS.ROLES_MANAGE,
  PERMISSIONS.SUBSCRIPTION_MANAGE,
]);

export const HOTEL_SCOPED_PERMISSION_CODES = new Set(
  Object.values(PERMISSIONS).filter((code) => !ORG_LEVEL_PERMISSION_CODES.has(code))
);

export const PERMISSION_LABELS = Object.freeze({
  [PERMISSIONS.DASHBOARD_VIEW]: 'Organisation Dashboard',
  [PERMISSIONS.REPORTS_VIEW]: 'Reports & Insights Exports',
  [PERMISSIONS.USERS_MANAGE]: 'User Management',
  [PERMISSIONS.ROLES_MANAGE]: 'Role Management',
  [PERMISSIONS.SUBSCRIPTION_MANAGE]: 'Subscription Management',
  [PERMISSIONS.WHATSAPP_COMMUNICATION_MANAGE]: 'WhatsApp Communication',
  [PERMISSIONS.BANQUET_CONFERENCE_MANAGE]: 'Hall',
  [PERMISSIONS.HALL_MANAGE]: 'Hall',
  [PERMISSIONS.BOOKING_OPERATIONS]: 'Booking Operations',
  [PERMISSIONS.BOOKING_MODIFY]: 'Modify Bookings',
  [PERMISSIONS.CUSTOMER_MANAGE]: 'Customer Management',
  [PERMISSIONS.HOUSEKEEPING_MANAGE]: 'Housekeeping',
  [PERMISSIONS.FINANCE_MANAGE]: 'Finance',
  [PERMISSIONS.CONFIGURATION_MANAGE]: 'Configuration',
  [PERMISSIONS.INVENTORY_MANAGE]: 'Daily Inventory',
  [PERMISSIONS.BOOKING_ENGINE_MANAGE]: 'Booking Engine',
  [PERMISSIONS.WEB_CHECKIN_MANAGE]: 'Web Check-in',
  [PERMISSIONS.AI_CHECKIN_MANAGE]: 'AI Check-in',
  [PERMISSIONS.AI_INVENTORY_MANAGE]: 'AI Inventory',
  [PERMISSIONS.STOCK_MANAGE]: 'Stock',
  [PERMISSIONS.DISTRIBUTION_MANAGE]: 'Distribution',
  [PERMISSIONS.CHANNELHUB_MANAGE]: 'Channel Hub',
  [PERMISSIONS.DYNAMIC_PRICING_MANAGE]: 'Dynamic Pricing',
  [PERMISSIONS.POS_RESTAURANT_MANAGE]: 'Restaurant & POS',
  [PERMISSIONS.BOOKING_AUDIT_VIEW]: 'Booking Audit Logs',
  [PERMISSIONS.POS_AUDIT_VIEW]: 'POS Order Audit Logs',
});

export const PERMISSION_GROUP_LABELS = Object.freeze({
  [PERMISSIONS.DASHBOARD_VIEW]: 'PMS',
  [PERMISSIONS.USERS_MANAGE]: 'Administration',
  [PERMISSIONS.ROLES_MANAGE]: 'Administration',
  [PERMISSIONS.SUBSCRIPTION_MANAGE]: 'Administration',
  [PERMISSIONS.WHATSAPP_COMMUNICATION_MANAGE]: 'PMS Add-ons',
  [PERMISSIONS.BANQUET_CONFERENCE_MANAGE]: 'Hall',
  [PERMISSIONS.HALL_MANAGE]: 'Hall',
  [PERMISSIONS.BOOKING_OPERATIONS]: 'PMS',
  [PERMISSIONS.BOOKING_MODIFY]: 'PMS',
  [PERMISSIONS.BOOKING_AUDIT_VIEW]: 'Audit Logs',
  [PERMISSIONS.CUSTOMER_MANAGE]: 'PMS',
  [PERMISSIONS.HOUSEKEEPING_MANAGE]: 'PMS',
  [PERMISSIONS.FINANCE_MANAGE]: 'PMS',
  [PERMISSIONS.CONFIGURATION_MANAGE]: 'PMS',
  [PERMISSIONS.INVENTORY_MANAGE]: 'PMS',
  [PERMISSIONS.BOOKING_ENGINE_MANAGE]: 'PMS Add-ons',
  [PERMISSIONS.WEB_CHECKIN_MANAGE]: 'PMS Add-ons',
  [PERMISSIONS.AI_CHECKIN_MANAGE]: 'AI Add-ons',
  [PERMISSIONS.AI_INVENTORY_MANAGE]: 'AI Add-ons',
  [PERMISSIONS.DYNAMIC_PRICING_MANAGE]: 'PMS',
  [PERMISSIONS.DISTRIBUTION_MANAGE]: 'Channel Manager',
  [PERMISSIONS.CHANNELHUB_MANAGE]: 'Channel Manager',
  [PERMISSIONS.POS_RESTAURANT_MANAGE]: 'Restaurant',
  [PERMISSIONS.POS_AUDIT_VIEW]: 'Audit Logs',
  [PERMISSIONS.STOCK_MANAGE]: 'Stock',
  [PERMISSIONS.REPORTS_VIEW]: 'Insights',
});

const humanizePermissionName = (value = '') =>
  String(value)
    .replace(/^Can\s+/i, '')
    .replace(/\bPOS\b/g, 'POS')
    .replace(/\bRBAC\b/g, 'RBAC')
    .trim();

export const getPermissionDisplayName = (permission) => {
  const code = typeof permission === 'string' ? permission : permission?.code;
  return PERMISSION_LABELS[code] || humanizePermissionName(permission?.name || code);
};

export const getPermissionDeniedMessage = (permission, fallbackLabel = 'this feature') => {
  const label = permission ? getPermissionDisplayName(permission) : fallbackLabel;
  return `Access restricted: your role does not include ${label}. Please contact the Owner to request access.`;
};

export const getPermissionGroupName = (permission) => {
  const code = typeof permission === 'string' ? permission : permission?.code;
  return PERMISSION_GROUP_LABELS[code] || 'Other';
};
